import { styleDefaults, styleKeys } from '../../../../shared/theme/styleSchema';
import { TABLE_STYLE_SCHEMA } from '../../../../shared/tables/tableStyleSchema';
import { DEFAULT_SITE_FONT } from '../../../../shared/theme/themeConfiguration';

/**
 * Styles de Tableau — un style n'est PAS une variante de rendu, c'est un jeu de
 * valeurs posées sur `design`. Rien n'est persisté qui dirait « ceci est un
 * comparatif » : choisir un style en recopie les valeurs dans `design`, et le
 * tableau reste « Personnalisé » (seul un style du thème se suit).
 *
 * Même mécanique que les styles de Carrousel (carouselStyles.js) et de Carte
 * info (infoCardStyles.js).
 *
 * Les réglages, ce qu'un style pose et leurs valeurs de base viennent du
 * schéma du Tableau (shared/tables/tableStyleSchema), partagé avec le
 * classique.
 */

/**
 * Ce qu'un style du THÈME du site pose : celles d'un style de la galerie, plus
 * les polices et couleurs de texte. Un thème dit « tableaux rouges en
 * Poppins » ; les styles génériques, eux, ne touchent pas aux polices.
 */
export const TABLE_THEME_STYLE_PROPS = styleKeys(TABLE_STYLE_SCHEMA, ['gallery', 'theme']);

/**
 * Base commune : chaque style ne réécrit que ce qui le distingue.
 *
 * Ces valeurs sont **exactement** les défauts historiques du bloc, augmentées
 * des props nouvelles — c'est ce qui permet à `withTableStyleDefaults` de
 * compléter un tableau enregistré sans changer son rendu. Ce n'est PAS le
 * réglage d'un tableau neuf : celui-là part du style « Classique »
 * (`TABLE_DEFAULT_STYLE_KEY`), qui s'en écarte.
 */
export const TABLE_STYLE_BASE = styleDefaults(TABLE_STYLE_SCHEMA);

const preset = (key, label, props) => ({
  key,
  label,
  props: { ...TABLE_STYLE_BASE, ...props },
});

/**
 * L'ordre est celui de la galerie : les deux styles tabulaires d'abord, puis
 * les deux dispositions en cartes.
 *
 * « Classique » est l'ancien style Encadré. L'ancien Classique — en-tête bleu
 * plein, bordures partout, zébrures — et « Zébré doux » ont été retirés : ils
 * font tableur, pas site web. Un tableau déjà réglé comme eux continue de
 * s'afficher tel quel ; la galerie le montre simplement en « Personnalisé ».
 */
export const TABLE_STYLE_PRESETS = [
  preset('classic', 'Classique', {
    headerBgColor: '#f1f5f9',
    headerTextColor: '#0f172a',
    useCustomBorder: true,
    cornerRadius: 12,
    isStripped: false,
  }),
  preset('clean', 'Épuré', {
    headerBgColor: 'transparent',
    headerTextColor: '#0f172a',
    headerUppercase: true,
    // Filets horizontaux seuls : ce sont les colonnes verticales qui donnent
    // l'air « tableur ».
    borderScope: 'rows',
    isStripped: false,
    highlightOnHover: false,
  }),
  preset('cards', 'Cartes', {
    layout: 'cards',
    // Une carte par ligne, qui liste TOUTES ses colonnes en couples
    // libellé / valeur. L'« en-tête » n'est plus une ligne : ce sont les
    // libellés posés devant chaque valeur, d'où un gris discret.
    //
    // `firstColumnEmphasis` reste disponible dans les réglages avancés : il
    // transforme la première cellule en titre de carte — mais son libellé de
    // colonne disparaît alors, ce qui ne peut pas être le comportement par
    // défaut.
    cardHeader: 'firstCell',
    headerBgColor: 'transparent',
    headerTextColor: '#64748b',
    headerUppercase: true,
    cornerRadius: 12,
    isStripped: false,
    highlightOnHover: false,
  }),
  preset('compare', 'Comparatif', {
    // Même disposition en cartes, coupée dans l'autre sens : une carte par
    // colonne, coiffée du titre de la colonne. C'est la grille tarifaire.
    layout: 'cards',
    cardHeader: 'columnTitle',
    headerBgColor: '#0f172a',
    headerTextColor: '#ffffff',
    cornerRadius: 12,
    strippedColor: '#f8fafc',
    highlightOnHover: false,
  }),
];

export const TABLE_DEFAULT_STYLE_KEY = 'classic';

/** Le style de clé donnée — les défauts du bloc s'en servent. */
export function tableStyleByKey(key) {
  return TABLE_STYLE_PRESETS.find((def) => def.key === key) || null;
}

/**
 * `design` complété par les valeurs de base.
 *
 * Indispensable : un tableau enregistré avant `layout` / `borderScope` /
 * `cornerRadius` ne les porte pas, et sans ce complément il ne correspondrait
 * à aucun style — tous les tableaux déjà en base s'afficheraient en
 * « Personnalisé ».
 *
 * `borderScope` se DÉDUIT de l'ancien `bordered` quand il manque : c'est lui
 * qui portait « pas de bordures » avant l'existence de la portée.
 */
export function withTableStyleDefaults(design) {
  const resolved = { ...TABLE_STYLE_BASE, ...(design || {}) };
  if (design?.borderScope == null) {
    resolved.borderScope = design?.bordered === false ? 'none' : 'all';
  }
  return resolved;
}

/**
 * Le `design` qu'un style produit : les réglages hors catalogue (pied, polices,
 * en-tête fixe, espacements) sont conservés tels quels. Essayer un style ne
 * doit rien détruire — et `columns` / `rows` ne sont même pas dans `design`.
 */
export function applyTableStyle(design, def) {
  return { ...(design || {}), ...def.props };
}

/**
 * Défauts du bloc = le style « Classique » posé sur la base + les réglages
 * hors catalogue. Un tableau neuf doit correspondre à une vignette de la
 * galerie, sinon il s'ouvre en « Personnalisé ».
 *
 * Les polices et les couleurs de texte n'ont plus de champ — le texte riche
 * les porte, cellule par cellule — mais gardent leur valeur : des sites
 * publiés s'en servent.
 */
export const DEFAULT_TABLE_DESIGN = {
  ...applyTableStyle(TABLE_STYLE_BASE, tableStyleByKey(TABLE_DEFAULT_STYLE_KEY)),
  textColorEven: '',
  textColorOdd: '',
  fontFamily: DEFAULT_SITE_FONT,
  cellFontFamily: DEFAULT_SITE_FONT,
  footer: false,
  footerData: '',
  stickyHeader: false,
  stickyHeaderOffset: 0,
  horizontalSpacing: '',
  verticalSpacing: '',
};

/**
 * Le `design` d'un bloc tel qu'il s'affiche : sans `design` enregistré, ce sont
 * les défauts du bloc. L'éditeur les fusionne (`defaultProps`), le `<Render>`
 * du site non ; et un réglage écrit sur `{}` faisait retomber le bloc sur la
 * base du schéma (en-tête bleu foncé). Tout ce qui lit ou réécrit `design`
 * part donc d'ici.
 */
export const storedTableDesign = (design) => design ?? DEFAULT_TABLE_DESIGN;

